import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

type SelfiePreviewTileProps = {
  stream?: MediaStream | null;
  visible: boolean;
  className?: string;
  onClick?: () => void;
  onHide?: () => void;
};

const SelfiePreviewTile: React.FC<SelfiePreviewTileProps> = ({
  stream,
  visible,
  className,
  onClick,
  onHide,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isInteractive = Boolean(onClick);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (stream) {
      video.srcObject = stream;
      video.play().catch(() => undefined);
    } else {
      video.srcObject = null;
    }

    return () => {
      if (video) {
        video.srcObject = null;
      }
    };
  }, [stream]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative flex h-[clamp(96px,18vw,140px)] w-[clamp(96px,18vw,140px)] items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-black/40 shadow-[0_18px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl',
        isInteractive ? 'cursor-pointer' : null,
        className
      )}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!isInteractive) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
      aria-label={isInteractive ? 'Turn off selfie preview' : undefined}
    >
      {onHide ? (
        <button
          type="button"
          className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-white/30 bg-black/55 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/80 hover:bg-black/70"
          aria-label="Hide selfie preview"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onHide();
          }}
        >
          x
        </button>
      ) : null}
      {stream ? (
        <video
          ref={videoRef}
          className="h-full w-full -scale-x-100 object-cover"
          autoPlay
          playsInline
          muted
        />
      ) : (
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Starting...</div>
      )}
    </div>
  );
};

export default SelfiePreviewTile;
